import { iklanTouchFromSearch, type IklanTouch } from "@shared/iklan";
import {
  campaignTouchFromSearch,
  pickCampaignTouch,
  searchClickKind,
  touchDiag,
  type CampaignTouch,
  type StoredCampaignTouch,
} from "@shared/insights";
import { getFbc } from "@/lib/fbParams";
import { fypSourceFromParam, fypSourceOk, type FypSource } from "@shared/fypSource";

/**
 * The last ad click (utm_* with a campaign or ad id) that brought this browser to
 * the store, kept 14 days so the order can be tallied against its campaign even
 * when the buyer checks out on a later visit. Same storage idea as the `_fbc`
 * click id in fbParams.ts, for the console's own per-campaign tally rather than
 * Meta's.
 */
const KEY = "shz_campaign_touch";

/** The click this page load saw, in case storage loses it before checkout. */
let captured: StoredCampaignTouch | null = null;
let bootKind: "c" | "f" | "-" | null = null;

/** Call at bootstrap, while the landing URL still carries the ad's parameters. */
export function captureCampaignFromUrl(): void {
  bootKind ??= searchClickKind(window.location.search);
  rememberFypVideo();
  const touch = campaignTouchFromSearch(window.location.search, Date.now());
  if (!touch) return;
  captured = touch;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(touch));
  } catch {
    // storage blocked: this page load still credits its own click from memory
  }
}

function readStored(): { raw: string | null; state: "ok" | "none" | "err" } {
  try {
    const raw = window.localStorage.getItem(KEY);
    return { raw, state: raw ? "ok" : "none" };
  } catch {
    return { raw: null, state: "err" };
  }
}

export function getCampaignTouch(): CampaignTouch {
  // Look at the URL again: the checkout keeps the landing's query, so a click the
  // bootstrap write lost is still here.
  captureCampaignFromUrl();
  return pickCampaignTouch(readStored().raw, captured, Date.now());
}

const credited = (t: CampaignTouch) => !!(t.utmCampaign || t.utmContent);

/** The touch to post with an order, and a line on how this browser held it. */
export function campaignTouchForOrder(): CampaignTouch & { touchDiag: string } {
  const now = Date.now();
  const before = readStored();
  const memory = captured;
  const touch = getCampaignTouch();
  const src = credited(pickCampaignTouch(before.raw, null, now))
    ? "s"
    : credited(pickCampaignTouch(null, memory, now))
      ? "m"
      : credited(touch)
        ? "u"
        : "-";
  let nav: string | null = null;
  try {
    nav = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type ?? null;
  } catch {
    nav = null;
  }
  let frame = false;
  try {
    frame = window.top !== window.self;
  } catch {
    frame = true;
  }
  return {
    ...touch,
    touchDiag: touchDiag({
      frame,
      storage: before.state,
      memory: !!memory,
      url: searchClickKind(window.location.search),
      boot: bootKind ?? "-",
      fbc: !!getFbc(),
      ageS: performance.now() / 1000,
      nav,
      src,
    }),
  };
}

const FYP_KEY = "shz_fyp_video";
const FYP_ARM_KEY = "shz_fyp_arm";
/** Written by the feed itself when a visit lands (src/components/fyp/fypSource.ts). */
const FYP_SRC_KEY = "shz_fyp_src";
const FYP_TEXTS_KEY = "shz_fyp_ft";
const FYP_LIVE_KEY = "shz_fyp_live";
/** ft=<cap>.<head>, '-' for none (109O S4). */
const fypTextsOf = (v: string | null): { cap: string | null; head: string | null } | null => {
  const m = v ? /^([a-f0-9]{12}|-)\.([a-f0-9]{12}|-)$/.exec(v) : null;
  if (!m || (m[1] === "-" && m[2] === "-")) return null;
  return { cap: m[1] === "-" ? null : m[1], head: m[2] === "-" ? null : m[2] };
};
const fypCode = (v: string | null) => (v && /^[a-z0-9]{7}$/.test(v) ? v : null);
const fypArm = (v: string | null) => (v === "c" || v === "r" ? v : null);

/** The visitor went into the Live from the FYP feed's LIVE card (109O S7): its checkouts in this tab are credited to the card. */
export function rememberFypLive(): void {
  try {
    window.sessionStorage.setItem(FYP_LIVE_KEY, "1");
  } catch {
    // storage blocked: the card's own Beli still carries fyplive=1
  }
}

/** A checkout the FYP feed's LIVE card opened (109O S7, fyplive=1): credited to the card, with the visitor's source. */
export function fypLiveForOrder(): { fypLive?: true; fypSrc?: FypSource } {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("fyplive") !== "1" && window.sessionStorage.getItem(FYP_LIVE_KEY) !== "1") return {};
    const saved = window.sessionStorage.getItem(FYP_SRC_KEY);
    const src = fypSourceFromParam(q.get("fs")) ?? (fypSourceOk(saved) ? saved : null);
    return { fypLive: true, ...(src ? { fypSrc: src } : {}) };
  } catch {
    return {};
  }
}

/** The FYP feed's text options for a video (109O S4), kept for the tab so a
 *  checkout opened through a product or sales page still credits them. */
export function rememberFypTexts(code: string, t: { cap: string | null; head: string | null }): void {
  try {
    window.sessionStorage.setItem(`${FYP_TEXTS_KEY}:${code}`, `${t.cap ?? "-"}.${t.head ?? "-"}`);
  } catch {
    // storage blocked: the checkout's address still carries them where it can
  }
}

/** A sales page a FYP video opened (?fyp=<code>) may send its buyer to a
 *  checkout that does not keep the address, such as an imported page's button:
 *  the video is remembered for this tab. */
function rememberFypVideo(): void {
  try {
    const q = new URLSearchParams(window.location.search);
    const code = fypCode(q.get("fyp"));
    if (code) window.sessionStorage.setItem(FYP_KEY, code);
    const arm = fypArm(q.get("fr"));
    if (code && arm) window.sessionStorage.setItem(FYP_ARM_KEY, arm);
    const src = fypSourceFromParam(q.get("fs"));
    if (code && src) window.sessionStorage.setItem(FYP_SRC_KEY, src);
    if (code && fypTextsOf(q.get("ft"))) window.sessionStorage.setItem(FYP_TEXTS_KEY, q.get("ft")!);
    // Opened from the FYP feed's LIVE card (109O S7).
    if (q.get("fyplive") === "1") window.sessionStorage.setItem(FYP_LIVE_KEY, "1");
  } catch {
    // storage blocked: the address still carries it where it can
  }
}

/** The FYP video whose bag opened this checkout: its code from this page's own
 *  address (the feed puts fyp=<code> on the checkout, product page or sales
 *  page it opens), else — only when the address has none — the one this tab
 *  came from, or nothing. A malformed value is dropped here, so it can never
 *  fail the order's check. */
export function fypVideoForOrder(): { fyp?: string; fypArm?: "c" | "r"; fypSrc?: FypSource; fypTexts?: { cap: string | null; head: string | null } } {
  try {
    const q = new URLSearchParams(window.location.search);
    const own = q.get("fyp");
    const code = own !== null ? fypCode(own) : fypCode(window.sessionStorage.getItem(FYP_KEY));
    // The order engine's group rides with the video (fr=), on the address or remembered with it.
    const arm = own !== null ? fypArm(q.get("fr")) : fypArm(window.sessionStorage.getItem(FYP_ARM_KEY));
    // Where the buyer came to the feed from (0048): on the address, else as the feed remembered it for this tab.
    const fromAddress = own !== null ? fypSourceFromParam(q.get("fs")) : null;
    const saved = window.sessionStorage.getItem(FYP_SRC_KEY);
    const src = fromAddress ?? (fypSourceOk(saved) ? saved : null);
    const texts = fypTextsOf(q.get("ft")) ?? (code ? fypTextsOf(window.sessionStorage.getItem(`${FYP_TEXTS_KEY}:${code}`)) : null) ?? (own === null ? fypTextsOf(window.sessionStorage.getItem(FYP_TEXTS_KEY)) : null);
    return code ? { fyp: code, ...(arm ? { fypArm: arm } : {}), ...(src ? { fypSrc: src } : {}), ...(texts ? { fypTexts: texts } : {}) } : {};
  } catch {
    return {};
  }
}

/** The Iklan HQ sheet that opened this checkout (iko, ikp, ikh on its address). */
export function iklanForOrder(): { iklan?: IklanTouch } {
  try {
    const touch = iklanTouchFromSearch(window.location.search);
    return touch ? { iklan: touch } : {};
  } catch {
    return {};
  }
}
